import {
  detectPaymentColumns, matchPaymentClient, PAYMENT_PROGRAM_MAX, PAYMENT_PROGRAM_SUGGESTIONS, readPaymentRows,
  type LedgerKind, type PayMethod, type PaymentColumns,
} from '@selfon/domain/browser';
import { useMemo, useState } from 'react';
import { api } from '../api';
import { Alert, Dialog, Field } from '../components/ui';
import { kindLabel, kindsFor, METHOD_LABEL } from '../format';
import { readTable, SheetError } from '../sheet';

/*
 * 결제내역 엑셀 올리기(수업별 결산). 엑셀은 이 브라우저에서만 읽는다.
 * 줄마다 입금자·기록사항 글자에 이용자 이름이 하나만 들어 있으면 자동으로 연결하고, 나머지는 직접 고른다.
 * 서버에는 연결한 줄의 이용자·결제일·금액과 이번에 고른 분류·결제 종류만 보낸다.
 */

type Client = { id: string; displayName: string; birthYear: number };
interface Line { line: number; paidOn: string; amount: number; text: string; clientId: string; auto: boolean; candidates: number; include: boolean }

const nf = (v: number) => v.toLocaleString('ko-KR');
const defaultKind = (program: string): LedgerKind => (program.trim() === '일반' ? 'SELF_PAY' : 'VOUCHER');

export function PaymentImport({ centerId, month, open, onClose, onDone }: { centerId: string; month: string; open: boolean; onClose: () => void; onDone: (msg: string) => void }) {
  const [clients, setClients] = useState<Client[] | null>(null);
  const [program, setProgram] = useState('');
  const [kind, setKind] = useState<LedgerKind>('VOUCHER');
  const [kindTouched, setKindTouched] = useState(false);
  const [method, setMethod] = useState<Exclude<PayMethod, 'VOUCHER_TERMINAL'>>('TRANSFER');
  const [monthBy, setMonthBy] = useState<'PAID' | 'VIEW'>('PAID');
  const [table, setTable] = useState<string[][] | null>(null);
  const [fileName, setFileName] = useState('');
  const [header, setHeader] = useState(0);
  const [cols, setCols] = useState<PaymentColumns | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setTable(null); setFileName(''); setCols(null); setLines([]); setError(null); };
  const close = () => { reset(); onClose(); };
  const effectiveKind = kindTouched && kindsFor(program.trim() !== '일반').includes(kind) ? kind : defaultKind(program);

  const build = (t: string[][], h: number, c: PaymentColumns, list: Client[]) => setLines(readPaymentRows(t, h, c).map((r) => {
    const m = matchPaymentClient(r.text, list);
    return { ...r, clientId: m.client?.id ?? '', auto: Boolean(m.client), candidates: m.candidates.length, include: Boolean(m.client) };
  }));

  const onFile = async (file: File | undefined) => {
    reset();
    if (!file) return;
    try {
      const list = clients ?? (await api.clients(centerId)).clients;
      setClients(list);
      const t = await readTable(file);
      const found = detectPaymentColumns(t);
      if (!found) { setError('날짜와 금액 머리글을 찾지 못했습니다. 결제일(또는 거래일시)·금액(또는 입금금액) 열이 있는 엑셀인지 확인하세요.'); return; }
      setTable(t); setFileName(file.name); setHeader(found.header); setCols(found.cols);
      build(t, found.header, found.cols, list);
    } catch (e) {
      setError(e instanceof SheetError ? e.message : '엑셀을 읽지 못했습니다. .xlsx, .xls, .csv 파일인지 확인하세요.');
    }
  };
  const changeCols = (next: PaymentColumns) => { setCols(next); if (table && clients) build(table, header, next, clients); };
  const setLine = (i: number, patch: Partial<Line>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const picked = lines.filter((l) => l.include && l.clientId);
  const total = picked.reduce((a, l) => a + l.amount, 0);
  const heads = useMemo(() => (table?.[header] ?? []).map((h, i) => ({ i, label: String(h || `${i + 1}번째 열`).trim() })), [table, header]);
  const sorted = useMemo(() => [...(clients ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName, 'ko')), [clients]);

  const submit = async () => {
    if (!program.trim()) throw new Error('결제 분류를 입력하세요. 예: 일반, 특수교육, 발달재활');
    if (!picked.length) throw new Error('저장할 줄이 없습니다. 이용자를 고르고 체크하세요.');
    const r = await api.importLedger(centerId, {
      program: program.trim(), kind: effectiveKind, method: effectiveKind === 'VOUCHER' ? 'VOUCHER_TERMINAL' : method,
      rows: picked.map((l) => ({ clientId: l.clientId, paidOn: l.paidOn, amount: l.amount, billingMonth: monthBy === 'PAID' ? l.paidOn.slice(0, 7) : month })),
    });
    reset();
    onDone(`'${program.trim()}' 결제 ${r.created}건(${nf(total)}원 중 저장분)을 넣었습니다.${r.skipped ? ` 이미 들어 있던 ${r.skipped}건은 건너뛰었습니다.` : ''}`);
  };

  return (
    <Dialog wide title="결제내역 엑셀 올리기" open={open} onClose={close} onSubmit={submit} submitLabel={`${picked.length}건 저장 (${nf(total)}원)`} hideSubmit={!lines.length}>
      <p className="hint">엑셀은 이 브라우저에서만 읽고 서버로 보내지 않습니다. 저장하는 것은 연결한 이용자·결제일·금액뿐입니다.</p>
      <div className="grid-2">
        <Field id="pi-program" label="결제 분류" hint="목록에서 고르거나 직접 적으세요.">
          <input id="pi-program" list="pi-programs" required maxLength={PAYMENT_PROGRAM_MAX} placeholder="예: 발달재활" value={program} onChange={(e) => setProgram(e.target.value)} />
          <datalist id="pi-programs">{PAYMENT_PROGRAM_SUGGESTIONS.map((p) => <option key={p} value={p} />)}</datalist>
        </Field>
        <Field id="pi-kind" label="결제 종류">
          <select id="pi-kind" value={effectiveKind} onChange={(e) => { setKind(e.target.value as LedgerKind); setKindTouched(true); }}>
            {kindsFor(program.trim() !== '일반').map((k) => <option key={k} value={k}>{kindLabel(k, program.trim() !== '일반')}</option>)}
          </select>
        </Field>
        {effectiveKind !== 'VOUCHER' && (
          <Field id="pi-method" label="결제 수단">
            <select id="pi-method" value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
              {(['TRANSFER', 'CARD', 'CASH'] as const).map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
            </select>
          </Field>
        )}
        <Field id="pi-month" label="어느 달 수납으로 넣을까요">
          <select id="pi-month" value={monthBy} onChange={(e) => setMonthBy(e.target.value as 'PAID' | 'VIEW')}>
            <option value="PAID">결제일이 속한 달</option>
            <option value="VIEW">보고 있는 달({month.slice(0, 4)}년 {Number(month.slice(5, 7))}월)</option>
          </select>
        </Field>
      </div>
      <Field id="pi-file" label="결제내역 엑셀" hint="은행 입출금 내역, 바우처 결제 내역 등. 날짜·금액·입금자(또는 기록사항) 열을 알아봅니다.">
        <input id="pi-file" type="file" accept=".xlsx,.xls,.csv,.htm,.html" onChange={(e) => void onFile(e.target.files?.[0])} />
      </Field>
      {error && <Alert>{error}</Alert>}
      {table && cols && (
        <>
          <fieldset className="pi-cols">
            <legend>{fileName} · 읽은 열</legend>
            <label>날짜 <select value={cols.date} onChange={(e) => changeCols({ ...cols, date: Number(e.target.value) })}>{heads.map((h) => <option key={h.i} value={h.i}>{h.label}</option>)}</select></label>
            <label>금액 <select value={cols.amount} onChange={(e) => changeCols({ ...cols, amount: Number(e.target.value) })}>{heads.map((h) => <option key={h.i} value={h.i}>{h.label}</option>)}</select></label>
            <span className="muted small">이름을 찾는 열: {cols.text.length ? cols.text.map((i) => heads[i]?.label).join(', ') : '없음(직접 고르세요)'}</span>
          </fieldset>
          <p className="small" role="status">
            {lines.length}줄 중 자동 연결 {lines.filter((l) => l.auto).length}줄 · 저장할 줄 {picked.length}건 {nf(total)}원
            {lines.some((l) => l.candidates > 1) && <span className="muted"> · 이름이 여럿 맞는 줄은 직접 고르세요</span>}
          </p>
          <div className="table-wrap pi-table">
            <table className="table acct-table">
              <caption className="sr-only">올린 결제내역 미리보기</caption>
              <thead><tr><th scope="col">저장</th><th scope="col">결제일</th><th scope="col" className="num">금액</th><th scope="col">엑셀 내용</th><th scope="col">이용자</th></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.line} className={l.include && l.clientId ? '' : 'muted'}>
                    <td><input type="checkbox" aria-label={`${l.line}번째 줄 저장`} checked={l.include} disabled={!l.clientId} onChange={(e) => setLine(i, { include: e.target.checked })} /></td>
                    <td>{l.paidOn}</td>
                    <td className="num">{nf(l.amount)}</td>
                    <td className="small">{l.text || '-'}</td>
                    <td>
                      <select aria-label={`${l.line}번째 줄 이용자`} value={l.clientId} onChange={(e) => setLine(i, { clientId: e.target.value, include: Boolean(e.target.value), auto: false })}>
                        <option value="">{l.candidates > 1 ? `여러 명 맞음 · 고르기` : '연결 안 함'}</option>
                        {sorted.map((c) => <option key={c.id} value={c.id}>{c.displayName} ({c.birthYear})</option>)}
                      </select>
                      {l.auto && <span className="chip chip-info pi-auto">자동</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="hint">같은 이용자·결제일·금액·분류로 이미 들어간 결제는 저장할 때 건너뜁니다. 저장한 결제는 수납 현황에서 환불(취소)할 수 있습니다.</p>
        </>
      )}
    </Dialog>
  );
}
